import React, { useEffect, useState } from 'react';
import { Modal } from '../ui';
import { useSiteChrome } from '../../contexts/siteChromeContext';
import { Property } from '../../types';
import {
  propertyAccentColor,
  propertyProgramCategoryLabel,
} from '../../utils/programLabels';
import ContactFormModal, { ContactFormData } from './ContactFormModal';

interface ProgramInfo {
  name: string;
  location: string;
  image: string;
  deliveryDate: string;
}

interface LotDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  lot: Property;
  program: ProgramInfo;
}

const LotDetailModal: React.FC<LotDetailModalProps> = ({
  isOpen,
  onClose,
  lot,
  program,
}) => {
  const { setLotDetailOpen } = useSiteChrome();
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);

  useEffect(() => {
    setLotDetailOpen(isOpen);
    return () => setLotDetailOpen(false);
  }, [isOpen, setLotDetailOpen]);
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  // Générer le type de logement (T2, T3, etc.)
  const getPropertyTypeLabel = () => {
    if (lot.rooms) {
      return `T${lot.rooms}`;
    }
    return `${lot.surface}m²`;
  };

  // Générer un étage (pour l'exemple)
  const getFloor = () => {
    const floors = ['1er', '2ème', '3ème', '4ème', '5ème'];
    const index = parseInt(lot.id) % floors.length;
    return floors[index];
  };

  // Générer une orientation (pour l'exemple)
  const getOrientation = () => {
    const orientations = ['Nord', 'Sud', 'Est', 'Ouest', 'Nord-Est', 'Sud-Ouest'];
    const index = (parseInt(lot.id) + 1) % orientations.length;
    return orientations[index];
  };

  // Générer un numéro d'appartement (pour l'exemple)
  const getApartmentNumber = () => {
    const baseNumber = parseInt(lot.id) * 10;
    return baseNumber < 100 ? `0${baseNumber}` : `${baseNumber}`;
  };

  // Vérifier si parking inclus
  const hasParking = lot.features.some(f => 
    f.toLowerCase().includes('parking') || f.toLowerCase().includes('garage')
  );

  const propertyColor = propertyAccentColor(lot.type);

  // URL d'exemple pour le plan de masse (à remplacer par une vraie URL)
  const floorPlanImage = 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=800&h=600&fit=crop';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      className="max-w-6xl"
    >
      <div className="space-y-4">
        {/* Photo du programme en haut */}
        <div className="flex items-center gap-3 pb-3 border-b border-neutral-200">
          <img
            src={program.image || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=200&h=150&fit=crop'}
            alt={program.name}
            className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 text-neutral-600 mb-0.5">
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="text-xs truncate">{program.location.split(' - ')[0]} – {program.location.split(' - ')[1]}</span>
            </div>
            <h3 
              className="text-base font-bold uppercase mb-0.5"
              style={{ color: propertyColor }}
            >
              {program.name}
            </h3>
            <span
              className="inline-flex mt-1 px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wide border-2 bg-white text-neutral-800"
              style={{ borderColor: propertyColor }}
            >
              {propertyProgramCategoryLabel(lot.type)}
            </span>
            <p className="text-xs text-neutral-600 mt-1.5">
              Livraison : {program.deliveryDate}
            </p>
          </div>
        </div>

        {/* Contenu principal : Plan de masse et informations */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Plan de masse (gauche) - 2 colonnes */}
          <div className="lg:col-span-2 space-y-3">
            <h4 className="text-base font-semibold text-neutral-900">
              Plan de masse
            </h4>
            <div className="relative w-full h-96 lg:h-[500px] rounded-lg overflow-hidden bg-neutral-100 border border-neutral-200">
              <img
                src={floorPlanImage}
                alt={`Plan de masse - ${getPropertyTypeLabel()}`}
                className="w-full h-full object-contain"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=800&h=600&fit=crop';
                }}
              />
              {/* Bouton télécharger le plan - à l'intérieur du cadre */}
              <button
                onClick={() => setIsContactFormOpen(true)}
                className="absolute bottom-4 right-4 text-white px-3 py-2 rounded-lg text-sm font-medium transition-opacity hover:opacity-90 duration-200 flex items-center justify-center gap-2 shadow-lg"
                style={{ backgroundColor: propertyColor }}
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
                Télécharger le plan
              </button>
            </div>

            {/* Container contact commercialisation */}
            {lot.contact && (
              <div className="bg-neutral-50 rounded-lg p-3 border border-neutral-200">
                <h5 className="text-sm font-semibold text-neutral-900 mb-2">
                  Contacter le commercial
                </h5>
                <div className="space-y-2">
                  <div>
                    <div className="text-sm font-medium text-neutral-900 mb-1">
                      {lot.contact.name}
                    </div>
                    <div className="text-xs text-neutral-600">
                      En charge de la commercialisation
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-neutral-700 flex-wrap">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: propertyColor }}>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      <a href={`tel:${lot.contact.phone}`} className="transition-opacity hover:opacity-80" style={{ color: propertyColor }}>
                        {lot.contact.phone}
                      </a>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: propertyColor }}>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <a href={`mailto:${lot.contact.email}`} className="transition-opacity hover:opacity-80 truncate" style={{ color: propertyColor }}>
                        {lot.contact.email}
                      </a>
                    </div>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={() => window.open(`mailto:${lot.contact?.email}`, '_self')}
                      className="flex-1 text-white px-2 py-1.5 rounded-lg text-xs font-medium transition-opacity hover:opacity-90 duration-200"
                      style={{ backgroundColor: propertyColor }}
                    >
                      Contacter
                    </button>
                    <button
                      onClick={() => window.open(`tel:${lot.contact?.phone}`, '_self')}
                      className="flex-1 bg-white border-2 hover:opacity-80 px-2 py-1.5 rounded-lg text-xs font-medium transition-opacity duration-200"
                      style={{ borderColor: propertyColor, color: propertyColor }}
                    >
                      Appeler
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Informations du lot (droite) - 1 colonne */}
          <div className="lg:col-span-1">
            <div className="bg-neutral-50 rounded-lg p-4 space-y-3 lg:mt-10">
              <h4 className="text-base font-semibold text-neutral-900">
                Informations du lot
              </h4>
              
              <div className="space-y-3">
                {/* Numéro d'appartement */}
                <div className="pb-3 border-b border-neutral-200">
                  <div className="text-xs text-neutral-600 mb-1">Numéro d'appartement</div>
                  <div className="text-xl font-bold" style={{ color: propertyColor }}>
                    {getApartmentNumber()}
                  </div>
                </div>

                {/* Type et surface */}
                <div>
                  <div className="text-xs text-neutral-600 mb-1">Type</div>
                  <div className="text-base font-semibold text-neutral-900">
                    {propertyProgramCategoryLabel(lot.type)} · {getPropertyTypeLabel()} · {lot.surface} m²
                  </div>
                </div>

                {/* Étage */}
                <div>
                  <div className="text-xs text-neutral-600 mb-1">Étage</div>
                  <div className="text-sm font-medium text-neutral-900">
                    {getFloor()}
                  </div>
                </div>

                {/* Orientation */}
                <div>
                  <div className="text-xs text-neutral-600 mb-1">Orientation</div>
                  <div className="text-sm font-medium text-neutral-900">
                    {getOrientation()}
                  </div>
                </div>

                {/* Prix */}
                <div className="pt-3 border-t border-neutral-200">
                  <div className="text-xs text-neutral-600 mb-1">Prix</div>
                  <div className="text-xl font-bold" style={{ color: propertyColor }}>
                    {formatPrice(lot.price)}
                  </div>
                  {hasParking && (
                    <div className="text-xs text-neutral-500 mt-1">
                      Parking inclus
                    </div>
                  )}
                </div>

                {/* Caractéristiques */}
                {lot.features && lot.features.length > 0 && (
                  <div className="pt-3 border-t border-neutral-200">
                    <div className="text-xs text-neutral-600 mb-2">Caractéristiques</div>
                    <div className="flex flex-wrap gap-1.5">
                      {lot.features.map((feature, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-white border border-neutral-300 rounded-full text-xs font-medium text-neutral-700"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de formulaire de contact */}
      <ContactFormModal
        isOpen={isContactFormOpen}
        onClose={() => setIsContactFormOpen(false)}
        onSubmit={(data: ContactFormData) => {
          // Logique pour enregistrer les données (API, analytics, etc.)
          console.log('Données du formulaire:', data);
          console.log('Lot:', lot);
          console.log('Programme:', program);
          
          // Télécharger le plan après soumission
          const link = document.createElement('a');
          link.href = floorPlanImage;
          link.download = `plan-masse-${getPropertyTypeLabel()}-${getApartmentNumber()}.jpg`;
          link.target = '_blank';
          link.click();
        }}
        variant="lead"
        submitLabel="Télécharger le plan"
        title="Télécharger le plan"
        description="Indiquez votre prénom, nom et e-mail pour télécharger le plan."
        primaryColor={propertyColor}
      />
    </Modal>
  );
};

export default LotDetailModal;

